import { useState } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Palette, Type, Image as ImageIcon, Building2, FileText, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { IdentityTab } from "@/components/admin/branding/IdentityTab";
import { ColorsTab } from "@/components/admin/branding/ColorsTab";
import { TypographyTab } from "@/components/admin/branding/TypographyTab";
import { LogosTab } from "@/components/admin/branding/LogosTab";
import { ContentTab } from "@/components/admin/branding/ContentTab";
import { useAppSettings } from "@/hooks/useAppSettings";
import { MARKETING_DOMAIN } from "@/lib/constants";

const TABS = [
  { id: "identity",   label: "Identity",   icon: Building2 },
  { id: "colors",     label: "Colors",     icon: Palette },
  { id: "typography", label: "Typography", icon: Type },
  { id: "logos",      label: "Logos",      icon: ImageIcon },
  { id: "content",    label: "Content",    icon: FileText },
] as const;

type TabId = (typeof TABS)[number]["id"];

export default function BrandingPage() {
  const [tab, setTab] = useState<TabId>("identity");
  const { data: settings } = useAppSettings();
  const marketingDomain = settings?.marketing_domain_url || MARKETING_DOMAIN;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-display text-3xl font-bold text-foreground">Branding</h1>
            <p className="text-muted-foreground mt-1 max-w-2xl">
              Edit colors, fonts, logos, and marketing copy. Changes are saved
              to the database and picked up by the public site on the next
              refresh.
            </p>
          </div>
          <Button variant="outline" size="sm" asChild>
            <a href={marketingDomain} target="_blank" rel="noreferrer">
              <ExternalLink className="h-4 w-4 mr-2" />
              Open marketing site
            </a>
          </Button>
        </div>

        <Tabs value={tab} onValueChange={(v) => setTab(v as TabId)}>
          <TabsList className="grid w-full grid-cols-5">
            {TABS.map((t) => (
              <TabsTrigger key={t.id} value={t.id} className="gap-2">
                <t.icon className="h-4 w-4" />
                <span className="hidden sm:inline">{t.label}</span>
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="identity" className="mt-6"><IdentityTab /></TabsContent>
          <TabsContent value="colors" className="mt-6"><ColorsTab /></TabsContent>
          <TabsContent value="typography" className="mt-6"><TypographyTab /></TabsContent>
          <TabsContent value="logos" className="mt-6"><LogosTab /></TabsContent>
          <TabsContent value="content" className="mt-6"><ContentTab /></TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
